import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import {
    getKpPageItemValues,
    updateKpMainLocalStore,
} from '@deltek/specification-client/core/Utilities/localstorageUtils'
import { SwitchButton } from '@deltek/specification-client/core/Widgets/ProductListing/Details/SwitchButton'
import Typography from '@material-ui/core/Typography'
import { Theme, makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import React, { useCallback, useEffect, useState } from 'react'
import {
    ProductListingDetailForm,
    SupportedOptionsObject,
} from '../components/ListingDetailsPage'
import { DetailProperties } from './DetailProperties'
import PremiumInfoIconTooltip from './PremiumInfoIconTooltip'
import { PropertiesEditable } from './Properties/PropertiesEditable'

const useStyles = makeStyles((theme: Theme) => ({
    containerFullWidth: {
        width: '100%',
    },
    fullViewHeading: {
        color: '#4F4F4F',
    },

    mainContent: {
        width: '100%',
        borderBottom: '6px solid #04325B',
        display: 'flex',
    },
    fullViewContainer: {
        width: 'fit-content',
        alignItems: 'center',
        marginLeft: 'auto',
    },
    title: {
        color: '#04325B',
        textTransform: 'uppercase',
        fontWeight: 500,
    },
}))

interface ListingDetailPropertiesProps {
    level?: string
    userId?: string
    editMode: boolean
    productListingDetailForm: ProductListingDetailForm
    isClearAllOptions: boolean
    handlePropertiesChange: (
        value: Record<string, Record<string, string[]>>
    ) => void
}

export const ListingDetailProperties: React.FC<ListingDetailPropertiesProps> = ({
    level,
    userId = '',
    editMode,
    productListingDetailForm,
    isClearAllOptions,
    handlePropertiesChange,
}) => {
    const classes = useStyles()
    const [fullView, setFullView] = useState(false)
    const localStorageKey = 'ProductDetailsPage'
    const localStorageItemKey = (userId: string) => `projectdetails${userId}`
    const handleChange = () => {
        setFullView(!fullView)
    }

    const selectedManufacturerOptionIds =
        Object.entries(productListingDetailForm.properties.normalizedProperties)
            .map(([_, property]) => {
                return {
                    ids: property?.flatMap((p) =>
                        (p?.optionGroups ?? []).flatMap((optGrp) =>
                            optGrp.options
                                ?.filter(
                                    (o) => o.isPremiumPlus && o.isSupported
                                )
                                .map((i) => i.optionId)
                        )
                    ),
                }
            })
            .flatMap((g) => g.ids) ?? []

    const handleClearAll = useCallback(() => {
        const properties = productListingDetailForm.properties
        const emptySelectedData = Object.entries(properties.value).reduce(
            (acc, [propertyName, property]) => {
                acc[propertyName] = Object.entries(property).reduce(
                    (subAcc, [subPropertyName, ids]) => {
                        const selectedNonMSIds =
                            ids?.filter((id) =>
                                selectedManufacturerOptionIds?.includes(id)
                            ) ?? []
                        subAcc[subPropertyName] = isClearAllOptions
                            ? []
                            : selectedNonMSIds
                        return subAcc
                    },
                    {} as Record<string, string[]>
                )
                return acc
            },
            {} as SupportedOptionsObject
        )
        handlePropertiesChange(emptySelectedData)
    }, [productListingDetailForm, handlePropertiesChange, isClearAllOptions])

    useEffect(() => {
        const propertyTypeLocalStore = getKpPageItemValues({
            currentUserId: userId,
            pageKey: localStorageKey,
        })
        if (propertyTypeLocalStore) {
            const storedData = JSON.parse(
                propertyTypeLocalStore[localStorageItemKey(userId)] as string
            )
            setFullView(storedData.fullView)
        }
    }, [userId])

    useEffect(() => {
        updateKpMainLocalStore({
            currentUserId: userId,
            pageKey: localStorageKey,
            itemKey: localStorageItemKey(userId),
            itemValue: JSON.stringify({
                fullView,
            }),
        })
    }, [fullView, productListingDetailForm, userId])

    if (!level || level === 'Basic') return null

    return (
        <GridContainer data-testid="ListingProperties">
            <GridItem className={classes.containerFullWidth}>
                <GridContainer
                    item
                    direction="row"
                    data-testid="detailProperties"
                >
                    {editMode ? (
                        <PropertiesEditable
                            clearAllSelection={handleClearAll}
                        />
                    ) : (
                        <>
                            <GridItem className={classes.mainContent} sm={12}>
                                <GridContainer>
                                    <GridItem md={7} sm={6} xs={12}>
                                        <Typography
                                            variant="h6"
                                            component="span"
                                            className={classes.title}
                                        >
                                            Properties
                                        </Typography>
                                        {processEnv.REACT_APP_EnableBadgelessCards !==
                                            '1' && <PremiumInfoIconTooltip />}
                                    </GridItem>

                                    <GridItem md={5} sm={6} xs={12}>
                                        <GridContainer
                                            className={clsx(
                                                classes.fullViewContainer
                                            )}
                                        >
                                            <SwitchButton
                                                checked={fullView}
                                                onChange={handleChange}
                                            />
                                            <Typography
                                                variant="body2"
                                                component="span"
                                                className={
                                                    classes.fullViewHeading
                                                }
                                            >
                                                View Full List of Properties
                                                Available
                                            </Typography>
                                        </GridContainer>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <DetailProperties
                                listingLevel={level}
                                isFullView={fullView}
                                properties={
                                    productListingDetailForm.properties
                                        .normalizedProperties ?? {}
                                }
                                selectedData={
                                    productListingDetailForm.properties.value ??
                                    {}
                                }
                            />
                        </>
                    )}
                </GridContainer>
            </GridItem>
        </GridContainer>
    )
}
